import { ProductAccordionItem } from "./ProductAccordionItem";
import { SaveBar } from "./SaveBar";
import { useProductDrafts } from "../hooks/useProductDrafts";
import { useSearchParams } from 'react-router-dom';
import ShippingAssignmentsPanel from "./ShippingAssignmentsPanel";
import FulfillmentSelectionPanel from "./FulfillmentSelectionPanel";
import { useEffect, useMemo, useState } from "react";
import { useShippingAssignments } from "../hooks/useShippingAssignments";
import schema from "../validationSchema";
import { useTaxAssignments } from "../hooks/useTaxAssignments";
import TaxesPanel from "./TaxesPanel";

export default function ProductManagerPanel() {
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business_id');

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const y = Math.abs(parseInt(body.style.top || "0", 10)) || window.scrollY || 0;

    html.style.overflow = "";
    html.style.overscrollBehaviorY = "";
    body.style.overflow = "";
    body.style.overscrollBehaviorY = "";
    body.style.position = "";
    body.style.top = "";
    body.style.width = "";

    requestAnimationFrame(() => window.scrollTo(0, y));
  }, []);

  const {
    drafts, addDraft, updateDraft, deleteDraft,
    diff, saveAll: saveProducts, busy: busyProducts, error: productsError
  } = useProductDrafts(businessId);

  const shipping = useShippingAssignments(businessId, drafts);
  const taxes = useTaxAssignments(businessId, drafts);

  const allFormsValid = useMemo(() => {
    return drafts.every((d) => schema.isValidSync(d, { abortEarly: false }));
  }, [drafts]);

  const pending = diff();

  const hasProductChanges = pending.added.length || pending.edited.length || pending.removed.length;
  const hasShippingChanges = shipping.hasChanges;
  const hasTaxChanges = taxes.hasChanges;
  const hasChanges = hasProductChanges || hasShippingChanges || hasTaxChanges;

  const [tab, setTab] = useState("products"); // "products" | "taxes" | "fulfillment" | "shipping"

  const busy = busyProducts || shipping.busy || taxes.busy;

  const saveAll = async () => {
    if (busy || !allFormsValid) return false;

    const shipV = shipping.validateBusinessRules(drafts);
    if (!shipV.ok) {
      shipping.setError(shipV.messages.join(" "));
      return false;
    }
    if (!taxes.validation.ok) {
      taxes.setError?.(taxes.validation.messages.join(" "));
      return false;
    }

    // 1) Save products; get client draft id -> server id map
    const { ok, idMap: productIdMap } = await saveProducts(shipping);
    if (!ok) return false;

    // 2) Save TAX CONFIGS first (so assignments to newly-added configs can be mapped)
    const { ok: cfgOK, idMap: taxIdMap } = await taxes.saveConfigChanges(); // returns {idMap}
    if (!cfgOK) return false;

    // 3) Save TAX ASSIGNMENTS (maps both product & tax ids, ignores drafts that didn’t persist)
    const taxOK = await taxes.saveAssignmentsIncludingDrafts({ productIdMap, taxIdMap });
    if (!taxOK) return false;

    await Promise.all([shipping.reload(), taxes.reload()]);
    return true;
  };

  const saveSummary = useMemo(() => {
    return {
      added: pending.added,
      edited: pending.edited,
      removed: pending.removed,
      shipping: {
        assignmentSelections: shipping.pendingCounts.totalAssignmentSelections,
        flagged: shipping.pendingCounts.flaggedCount,
        includesDrafts: shipping.pendingCounts.hasDraftIds,
      }
    };
  }, [pending, shipping.pendingCounts]);

  const padForBar = hasChanges ? "pb-28 md:pb-32" : "";

  // simple view switch (render exactly one panel)
  const renderPanel = () => {
    if (tab === "taxes") {
      return (
        <TaxesPanel
          products={drafts}
          taxes={taxes}
        />
      );
    }
    if (tab === "fulfillment") {
      return (
        <FulfillmentSelectionPanel
          products={drafts}
          shipping={shipping}
          onSaveAll={saveAll}
        />
      );
    }
    if (tab === "shipping") {
      return (
        <ShippingAssignmentsPanel
          businessId={businessId}
          products={drafts}
          shipping={shipping}
          onSaveAll={saveAll}
        />
      );
    }
    // products
    return (
      <>
        {/* Add Product button BELOW the tabs, like your 2nd screenshot */}
        <div className="mb-3">
          <button onClick={addDraft} className="btn-primary rounded-xl">+ Add Product</button>
        </div>

        <ul className="space-y-3">
          {drafts.map((p) => (
            <ProductAccordionItem
              key={p.id}
              product={p}
              onChange={(patch) => updateDraft(p.id, patch)}
              onDelete={() => deleteDraft(p.id)}
              businessId={businessId}
              baseCurrency={shipping.baseCurrency}
            />
          ))}
        </ul>
      </>
    );
  };

  return (
    <section
      className={`p-6 space-y-4 ${hasChanges ? padForBar : ""}`}
      style={{
        touchAction: "pan-y",
        WebkitOverflowScrolling: "touch",
        overflowX: "hidden",
      }}
    >
      {/* Title */}
      <h1 className="text-2xl font-semibold">Products</h1>

      {/* Tabs: underline style like your 2nd screenshot */}
      <nav className="border-b">
        <ul className="flex gap-6 text-sm">
          {[
            ["products", "Products"],
            ["taxes", "Taxes"],
            ["fulfillment", "Delivery & Takeaway"],
            ["shipping", "Shipping & Orders"],
          ].map(([val, label]) => {
            const active = tab === val;
            return (
              <li key={val}>
                <button
                  className={`py-2 -mb-px border-b-2 transition ${
                    active ? "border-indigo-600 text-indigo-700 font-medium"
                           : "border-transparent text-gray-600 hover:text-gray-800"
                  }`}
                  onClick={() => setTab(val)}
                >
                  {label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {productsError && <p className="text-red-600">{productsError}</p>}
      {shipping.error && <p className="text-red-600">{shipping.error}</p>}
      {taxes.error && <p className="text-red-600">{taxes.error}</p>}

      {renderPanel()}

      {hasChanges && (
        <SaveBar
          busy={busy}
          onSave={saveAll}
          summary={saveSummary}
          disabled={!allFormsValid || !taxes.validation.ok}
        />
      )}
    </section>
  );
}
